import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ArrowLeft, ShieldCheck, Ticket, Building2, BarChart3, Wallet, BusFront, ChevronRight } from "lucide-react";
import { Crest, FlagStrip } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Captcha from "@/components/Captcha";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import PasswordInput from "@/components/PasswordInput";
import AppDownloads from "@/components/AppDownloads";
import { api, errMsg } from "@/lib/api";
import { useAuth, userRoles, roleHome } from "@/context/AuthContext";

const STAFF_ROLES = [
  { role: "teller", label: "Teller", desc: "Sell tickets at the counter", icon: Ticket, tint: "bg-blue-50 text-[#0052FF]" },
  { role: "station_master", label: "Station Master", desc: "Trips, buses, drivers & sign-outs", icon: Building2, tint: "bg-orange-50 text-orange-600" },
  { role: "manager", label: "Management", desc: "Financial dashboards & reports", icon: BarChart3, tint: "bg-indigo-50 text-indigo-600" },
  { role: "cashier", label: "Cashier", desc: "Handovers, vault & bank deposits", icon: Wallet, tint: "bg-emerald-50 text-emerald-600" },
  { role: "bus_owner", label: "Bus Owner", desc: "Performance of your fleet", icon: BusFront, tint: "bg-sky-50 text-sky-600" },
  { role: "admin", label: "Super Admin", desc: "Full access to the whole system", icon: ShieldCheck, tint: "bg-rose-50 text-rose-600", super: true },
];

function RoleLogin({ meta, onBack }) {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [login, setLogin] = useState({ identifier: "", password: "" });
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const Icon = meta.icon;

  const submit = async (e) => {
    e.preventDefault();
    if (!ok) return toast.error("Please solve the code challenge first");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { ...login, scope: "staff" });
      if (!userRoles(data.user).includes(meta.role)) {
        toast.error(`This account is not a ${meta.label} account. Use the correct portal.`);
        return;
      }
      signIn(data.token, data.user);
      toast.success(`Welcome, ${data.user.name.split(" ")[0]}`);
      nav(location.state?.next || roleHome[meta.role] || "/");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto w-full max-w-md" data-testid={`staff-login-${meta.role}`}>
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0052FF]" data-testid="staff-login-back">
        <ArrowLeft className="h-4 w-4" />All staff portals
      </button>
      <div className="card-soft overflow-hidden">
        <FlagStrip />
        <div className="p-6">
          <div className="mb-5 flex items-center gap-3">
            <span className={`grid h-12 w-12 place-items-center rounded-2xl ${meta.tint}`}><Icon className="h-6 w-6" /></span>
            <div>
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#182678]" data-testid="staff-login-title">{meta.label} Login</h2>
              <p className="text-sm text-slate-500">{meta.desc}</p>
            </div>
          </div>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Email or phone</Label>
              <Input className="h-11" value={login.identifier} onChange={(e) => setLogin({ ...login, identifier: e.target.value })} data-testid="staff-login-identifier-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Password</Label>
              <PasswordInput className="h-11" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} testid="staff-login-password-input" /></div>
            <Captcha onValid={setOk} testid="staff-login-captcha" />
            <Button type="submit" disabled={busy || !ok} className="h-12 w-full rounded-xl bg-[#0052FF] text-base font-bold hover:bg-[#0041CC] btn-press disabled:opacity-60" data-testid="staff-login-submit-button">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : `Login to ${meta.label}`}
            </Button>
            <button type="button" onClick={() => setForgot(true)} className="w-full text-center text-sm font-semibold text-[#0052FF] hover:underline" data-testid="staff-forgot-password-link">Forgot password?</button>
            <p className="text-center text-xs text-slate-400">Staff accounts are created by an administrator.</p>
          </form>
        </div>
      </div>
      <ForgotPasswordDialog open={forgot} onOpenChange={setForgot} scope="staff" captchaId="staff-forgot-captcha" note={`${meta.label} accounts · SMS sent to the number on file`} />
    </div>
  );
}

export default function StaffPortal() {
  const nav = useNavigate();
  const [selected, setSelected] = useState(null);
  const meta = STAFF_ROLES.find((r) => r.role === selected);

  return (
    <div className="hero-bg relative min-h-full overflow-hidden">
      <Crest variant="navy" className="pointer-events-none absolute -right-32 top-6 h-[520px] w-[520px] opacity-[0.04]" />
      <div className="relative mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <div className="mb-8 flex flex-col items-center text-center" data-testid="staff-portal">
          <span className="mb-4 grid h-16 w-16 place-items-center rounded-full bg-white shadow-[0_12px_28px_-14px_rgba(24,38,120,0.7)] ring-1 ring-slate-200"><Crest className="h-14 w-14" /></span>
          <div className="label-xs">GPRTU · Developed by Nacasky Co. Ltd</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#182678] sm:text-4xl" data-testid="staff-portal-title">Staff &amp; Administration</h1>
          <p className="mt-1 max-w-xl text-sm text-slate-500">Choose your portal to sign in. Each portal is for its own role; the Super Admin portal has full access to the whole system.</p>
        </div>

        {meta ? (
          <RoleLogin meta={meta} onBack={() => setSelected(null)} />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {STAFF_ROLES.map((r) => {
                const Icon = r.icon;
                return (
                  <button key={r.role} onClick={() => setSelected(r.role)} data-testid={`staff-portal-card-${r.role}`}
                    className={`card-soft group flex items-center gap-4 p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg ${r.super ? "ring-2 ring-rose-200" : ""}`}>
                    <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${r.tint}`}><Icon className="h-6 w-6" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-display text-lg font-extrabold text-[#182678]"><span className="whitespace-nowrap">{r.label}</span>{r.super && <span className="chip bg-rose-100 text-rose-700">Full access</span>}</span>
                      <span className="block text-xs text-slate-500">{r.desc}</span>
                    </span>
                    <ChevronRight className="h-5 w-5 shrink-0 text-slate-300 transition-colors group-hover:text-[#0052FF]" />
                  </button>
                );
              })}
            </div>
            <div className="mt-8 flex flex-col items-center gap-2">
              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500" data-testid="staff-secured-by"><Lock className="h-3.5 w-3.5" />Secure GPRTU sign-in · Developed by Nacasky Co. Ltd</p>
              <button onClick={() => nav("/")} className="text-sm font-semibold text-[#0052FF] hover:underline" data-testid="staff-to-customer-link">← Back to the passenger site</button>
            </div>
            <AppDownloads />
          </>
        )}
      </div>
    </div>
  );
}
